// 이리 라이브 도우미의 게임 사이클 상태 (순수 함수).
//
// 대기(waiting)   : PiP에 [조합 캡처] [게임 시작]과 조합 분석을 보여준다.
// 게임 중(inGame) : [게임 종료]로 다시 대기가 된다.
//
// PiP는 클릭 통과가 불가능해서 게임 화면 위에 두면 게임 클릭을 방해한다. 그래서 두 가지 사용 방식을 지원한다.
// - 다중 모니터(기본, pipInGame=true): PiP를 게임이 없는 다른 모니터에 두고 게임 중에도 계속 보여준다.
// - 단일 모니터(pipInGame=false): 게임 중에는 PiP를 닫아 게임 클릭을 방해하지 않는다.

export const PHASE = {
  WAITING: 'waiting',
  IN_GAME: 'inGame',
};

// 조합 캡처 인식 상태 (idle → running → done | error)
export const RECOGNITION = {
  IDLE: 'idle',
  RUNNING: 'running',
  DONE: 'done',
  ERROR: 'error',
};

const idleRecognition = () => ({ status: RECOGNITION.IDLE, teams: null, error: null });

// 조합 창에서 맨 위에 따로 분리돼 보이는 1팀이 항상 우리 팀이다(사용자 확인). 팀 번호는 0부터.
export const MY_TEAM_INDEX = 0;

export function createInitialState(overrides = {}) {
  return {
    phase: PHASE.WAITING,
    pipHidden: false, // 웹에서 [PiP 숨김]을 눌렀거나 사용자가 PiP를 직접 닫은 상태
    pipInGame: true, // 게임 중에도 PiP를 유지할지 (다중 모니터면 true, 단일 모니터면 false)
    compFrame: null, // 마지막 조합 캡처 { blob, url, width, height, capturedAt }
    recognition: idleRecognition(), // 조합 캡처 인식 결과: teams는 팀별 카드 배열
    selectedTeam: null, // PiP에서 상세를 보고 있는 팀 번호 (null이면 전체 팀 요약)
    ...overrides,
  };
}

export function liveAssistReducer(state, action) {
  switch (action.type) {
    case 'COMP_CAPTURED':
      // 조합 캡처는 대기 상태에서만. 다시 캡처하면 교체한다.
      // 새 캡처는 이전 인식 결과와 팀 선택을 지운다(인식 중이던 이전 결과는 도착해도 무시된다).
      return state.phase === PHASE.WAITING
        ? { ...state, compFrame: action.frame, recognition: idleRecognition(), selectedTeam: null }
        : state;
    case 'RECOGNITION_STARTED':
      return { ...state, recognition: { status: RECOGNITION.RUNNING, teams: null, error: null }, selectedTeam: null };
    case 'RECOGNITION_SUCCEEDED':
      // 캡처가 교체돼 인식 중이 아니면 늦게 도착한 결과를 버린다.
      return state.recognition.status === RECOGNITION.RUNNING
        ? { ...state, recognition: { status: RECOGNITION.DONE, teams: action.teams, error: null } }
        : state;
    case 'RECOGNITION_FAILED':
      return state.recognition.status === RECOGNITION.RUNNING
        ? { ...state, recognition: { status: RECOGNITION.ERROR, teams: null, error: action.message } }
        : state;
    case 'TEAM_SELECTED': {
      // 같은 팀을 다시 누르면 전체 요약으로 돌아간다.
      const { teams } = state.recognition;
      if (!teams || action.index < 0 || action.index >= teams.length) {
        return state;
      }
      return { ...state, selectedTeam: state.selectedTeam === action.index ? null : action.index };
    }
    case 'GAME_STARTED':
      // 조합 캡처는 게임 중 웹에서 계속 참고할 수 있게 유지한다.
      return state.phase === PHASE.WAITING ? { ...state, phase: PHASE.IN_GAME } : state;
    case 'GAME_ENDED':
      // 다음 판은 새 조합이므로 조합 캡처를 비운다.
      return state.phase === PHASE.IN_GAME
        ? { ...state, phase: PHASE.WAITING, compFrame: null, recognition: idleRecognition(), selectedTeam: null }
        : state;
    case 'PIP_HIDDEN_CHANGED':
      return { ...state, pipHidden: action.hidden };
    case 'PIP_IN_GAME_CHANGED':
      return { ...state, pipInGame: action.enabled };
    default:
      return state;
  }
}

// PiP는 숨김이 아니고, 대기 상태이거나 "게임 중에도 PiP 유지"가 켜져 있을 때 보인다.
export function shouldShowPip(state) {
  return !state.pipHidden && (state.phase === PHASE.WAITING || state.pipInGame);
}
